import { Owner } from 'src/Utils/globalEnums'
import { useMemo } from 'react'
import {
  Control,
  CONTROL_STATUS,
} from '@trustero/trustero-api-web/lib/model/control_pb'
import { AuditReadinessRecord } from '@trustero/trustero-api-web/lib/audit/auditbot_pb'
import { useComplianceFrameworks } from 'src/components/async/complianceframework'
import { labelNamesForSearch, useLabelMap } from 'src/context/labelMap'
import { getControlStatusName } from 'src/components/PageLayout/ShowPage/Dropdowns/Dropdowns.constants'
import { useControlStatuses } from 'src/pages/AccountSettings/AccountSettings.hooks'
import { GatekeeperFlag, IsGatekeeperEnabled } from 'src/context/Gatekeeper'
import { getViewInSourceLabel } from 'src/components/Reusable/Evidence/sourceLabel'
import {
  LINKABLE_RECEPTOR_MODEL_IDS,
  LIST_ITEM_COPY,
} from 'src/components/Reusable/ListItem/ListItem.constants'
import { getControlEffectivenessText } from 'src/pages/Controls/IndexItem/ControlsListItem.helpers'
import { prepareSearchableData } from 'src/Utils/search/search.helpers'
import { SearchableData } from 'src/Utils/search/search.types'
import {
  ControlToEvidenceMapping,
  ControlToEvidenceMappingInfo,
} from '@trustero/trustero-api-web/lib/evidencemgmt/evidencemgmt_pb'

export const useSearchableControls = (
  controls: Control[],
  // Latest control test results, when the grid shows the Operating Effectiveness column.
  auditReadinessList?: AuditReadinessRecord[],
): SearchableData<Control>[] => {
  const { data: cfs } = useComplianceFrameworks(true)
  const labelMap = useLabelMap()
  const { data: customStatuses } = useControlStatuses()
  const isMultiPolicyEnabled = IsGatekeeperEnabled(
    GatekeeperFlag.MULTI_POLICY_CONTROL_LINKAGES,
  )

  const searchableControls = useMemo(() => {
    const complianceFrameworks = cfs?.getItemsList() || []
    const frameworkNameById = new Map(
      complianceFrameworks.map((framework) => [
        framework.getId(),
        framework.getName(),
      ]),
    )
    const testsByControlId = new Map(
      (auditReadinessList || []).map((readiness) => [
        readiness.getControlId(),
        readiness.getControlTestsList(),
      ]),
    )
    return prepareSearchableData(
      controls,
      (control: Control) => control.getId(),
      (control: Control) => {
        const department = control.getDepartmentShortName()
        const modelId = control.getModelId()
        const name = control.getName()
        const frameworkIds = control.getComplianceFrameworkIdsList()
        const policyName = control.getPolicyName() || LIST_ITEM_COPY.NO_POLICY
        // With several linked policies the row lists each one and its department.
        const linkedPolicies = isMultiPolicyEnabled
          ? control
              .getLinkedPoliciesList()
              .map(
                (policy) =>
                  `${policy.getPolicyName()} ${policy.getDepartment()}`,
              )
              .join(' ')
          : ''
        const namesForControl = frameworkIds
          .map((frameworkId) => frameworkNameById.get(frameworkId))
          .filter((frameworkName): frameworkName is string => !!frameworkName)
        const complianceFrameworkNames =
          namesForControl.length > 0
            ? namesForControl.join(', ')
            : 'No Framework'
        // The status the row shows, which may be one of the account's own.
        const status = getControlStatusName(
          control.getStatus(),
          control.getCustomStatusId(),
          customStatuses?.getItemsList(),
        )
        // "Unassigned" is about the owner; assignees are optional.
        const owner = control.getOwnerEmail() || Owner.UNASSIGNED
        const assignees = control.getAssigneeEmailsList().join(' ')
        const linkedRisks = (control.getLinkedRisksList() || [])
          .map((r) => `${r.getCustomId()} ${r.getName()}`.trim())
          .join(' ')
        const receptorModelId = control.getImportReceptorModelId()
        const source = control.getImportLink()
          ? getViewInSourceLabel(receptorModelId)
          : LINKABLE_RECEPTOR_MODEL_IDS.includes(receptorModelId)
          ? LIST_ITEM_COPY.NOT_LINKED
          : ''
        const effectiveness = auditReadinessList
          ? getControlEffectivenessText(
              testsByControlId.get(control.getId()),
              control.getStatus() === CONTROL_STATUS.NOTAPPLICABLE,
            )
          : ''

        return [
          department,
          modelId,
          name,
          policyName,
          linkedPolicies,
          source,
          complianceFrameworkNames,
          status,
          effectiveness,
          owner,
          assignees,
          linkedRisks,
          labelNamesForSearch(control.getLabelIdsList(), labelMap),
        ]
      },
      (control) => {
        return [control.getContentId()]
      },
    )
  }, [
    controls,
    cfs,
    labelMap,
    customStatuses,
    isMultiPolicyEnabled,
    auditReadinessList,
  ])

  return searchableControls
}

export const useSearchableControlToEvidenceMappings = (
  controlToEvidenceMappings: ControlToEvidenceMapping[],
): SearchableData<ControlToEvidenceMapping>[] => {
  return useMemo(() => {
    return prepareSearchableData(
      controlToEvidenceMappings,
      (controlToEvidenceMapping: ControlToEvidenceMapping) =>
        controlToEvidenceMapping.getControl()?.getId() || '',
      (controlToEvidenceMapping: ControlToEvidenceMapping) => {
        return [
          controlToEvidenceMapping.getControl()?.getDepartmentShortName(),
          controlToEvidenceMapping.getControl()?.getModelId(),
          controlToEvidenceMapping.getControl()?.getName(),
          ...controlToEvidenceMapping
            .getEvidenceList()
            .map((evidence) => evidence.getCaption()),
        ]
      },
      (controlToEvidenceMapping) => {
        return [controlToEvidenceMapping.getControl()?.getContentId() || '']
      },
    )
  }, [controlToEvidenceMappings])
}

export const useSearchableControlToEvidenceMappingInfo = (
  controlToEvidenceMappingInfo: ControlToEvidenceMappingInfo[],
): SearchableData<ControlToEvidenceMappingInfo>[] => {
  return useMemo(() => {
    return prepareSearchableData(
      controlToEvidenceMappingInfo,
      (controlToEvidenceMappingInfo: ControlToEvidenceMappingInfo) =>
        controlToEvidenceMappingInfo.getControl()?.getId() || '',
      (controlToEvidenceMappingInfo: ControlToEvidenceMappingInfo) => {
        return [
          controlToEvidenceMappingInfo.getControl()?.getDepartmentShortName(),
          controlToEvidenceMappingInfo.getControl()?.getModelId(),
          controlToEvidenceMappingInfo.getControl()?.getName(),
          controlToEvidenceMappingInfo
            .getGapEvidenceMappingsList()
            .length.toString(),
          controlToEvidenceMappingInfo
            .getLinkedEvidenceList()
            .length.toString(),
          controlToEvidenceMappingInfo
            .getSuggestedEvidenceList()
            .length.toString(),
          controlToEvidenceMappingInfo
            .getGapEvidenceMappingsList()
            .map((gapEvidenceMapping) => gapEvidenceMapping.getGap())
            .join(', '),
          controlToEvidenceMappingInfo
            .getGapEvidenceMappingsList()
            .map((gapEvidenceMapping) =>
              gapEvidenceMapping
                .getEvidencesList()
                .map((evidence) => evidence.getCaption())
                .join(', '),
            )
            .join(', '),
          controlToEvidenceMappingInfo
            .getLinkedEvidenceList()
            .map((linkedEvidence) => linkedEvidence.getCaption())
            .join(', '),
          controlToEvidenceMappingInfo
            .getSuggestedEvidenceList()
            .map((suggestedEvidence) => suggestedEvidence.getCaption())
            .join(', '),
        ]
      },
    )
  }, [controlToEvidenceMappingInfo])
}
